import { revalidateLogic, useForm } from '@tanstack/react-form'
import {
  gastoSchema,
  type GastoFormValues,
  DEFAULT_GASTO_VALUES,
} from './gastoSchema'

interface UseGastoFormOptions {
  defaultValues?: Partial<GastoFormValues>
  onSubmit: (values: GastoFormValues) => void | Promise<void>
}

/**
 * Mapea los issues de Zod a la forma `{ [fieldName]: message }` que TanStack Form
 * distribuye a `field.state.meta.errors` de cada campo. Sin esto, un validador
 * form-level que devuelve una lista suelta bloquea el submit pero no muestra el
 * error en ningún campo (ORR-136: "es como si no funcionara el click").
 * Conserva el primer error por campo.
 */
export function zodIssuesToFieldErrors(
  issues: ReadonlyArray<{ path: PropertyKey[]; message: string }>
): Record<string, string> {
  const fields: Record<string, string> = {}
  for (const issue of issues) {
    const key = issue.path.join('.')
    if (key && !(key in fields)) {
      fields[key] = issue.message
    }
  }
  return fields
}

export function useGastoForm({ defaultValues, onSubmit }: UseGastoFormOptions) {
  const form = useForm({
    defaultValues: {
      ...DEFAULT_GASTO_VALUES,
      ...defaultValues,
    } as GastoFormValues,
    // Valida al enviar y, después del primer intento, en cada cambio, así un error entre campos se limpia al corregir el otro campo.
    validationLogic: revalidateLogic(),
    validators: {
      // Validator manual (no pasar el schema directo): `z.coerce.number()` produce
      // un input type `unknown` que rompe el typing de StandardSchema de TanStack Form.
      // Devolvemos `{ fields }` para que cada error se muestre en su propio campo
      // (formato canónico de TanStack Form para errores por campo desde el form).
      onDynamic: ({ value }) => {
        const result = gastoSchema.safeParse(value)
        if (result.success) return undefined
        return { fields: zodIssuesToFieldErrors(result.error.issues) }
      },
    },
    onSubmit: async ({ value }) => {
      await onSubmit(value)
    },
  })

  return { form }
}
